import axios from "axios";
import { partnerBase } from "../base";
import { displayRole, roleKey } from "../pages/PartnerPortal/partnerRoleCatalog";

const AUTH_BASE = `${partnerBase}/api/auth`;
const PARTNER_BASE = `${partnerBase}/api/partner`;
// Session creation/lookup lives under /api/sessions (shared with
// server-to-server API-key callers), not under /api/partner — only
// dashboard-only endpoints (keys, billing, webhooks, branding) are mounted
// there. Candidate Fit is its own top-level resource, /api/candidate-fit.
const SESSIONS_BASE = `${partnerBase}/api/sessions`;
const CANDIDATE_FIT_BASE = `${partnerBase}/api/candidate-fit`;

// Partner sessions use their own storage keys, deliberately separate from
// the main platform's client/talent "token" — a client logged into the main
// app must never be treated as an authenticated partner just because a
// generic "token" key happens to be present, and signing into one must
// never clobber or be clobbered by the other. Partner access is
// login-only: credentials come from an emailed invite/reset link or an
// admin-provisioned key, never from an existing main-platform session.
export const PARTNER_TOKEN_KEY = "partner_token";
export const PARTNER_CLIENT_KEY = "partner_client";

export const getPartnerToken = () =>
  localStorage.getItem(PARTNER_TOKEN_KEY) ||
  sessionStorage.getItem(PARTNER_TOKEN_KEY) ||
  "";

export const setPartnerSession = (token, client, rememberMe = false) => {
  const store = rememberMe ? localStorage : sessionStorage;
  store.setItem(PARTNER_TOKEN_KEY, token || "");
  store.setItem(PARTNER_CLIENT_KEY, JSON.stringify(client || {}));
};

export const clearPartnerSession = () => {
  localStorage.removeItem(PARTNER_TOKEN_KEY);
  localStorage.removeItem(PARTNER_CLIENT_KEY);
  sessionStorage.removeItem(PARTNER_TOKEN_KEY);
  sessionStorage.removeItem(PARTNER_CLIENT_KEY);
};

export const getStoredPartnerClient = () => {
  try {
    return JSON.parse(
      localStorage.getItem(PARTNER_CLIENT_KEY) ||
        sessionStorage.getItem(PARTNER_CLIENT_KEY) ||
        "null",
    );
  } catch {
    return null;
  }
};

const authHeaders = () => ({
  Authorization: `Bearer ${getPartnerToken()}`,
});

export const getPartnerAuthError = (error) => {
  return (
    error?.response?.data?.message ||
    "Something went wrong. Please check your connection and try again."
  );
};

export const partnerLogin = async ({ email, password, rememberMe }) => {
  const response = await axios.post(`${AUTH_BASE}/login`, { email, password, rememberMe });
  return response;
};

export const partnerForgotPassword = async ({ email }) => {
  const response = await axios.post(`${AUTH_BASE}/forgot-password`, { email });
  return response;
};

export const partnerResetPassword = async ({ token, password }) => {
  const response = await axios.post(`${AUTH_BASE}/reset-password`, {
    token,
    password,
  });
  return response;
};
export const partnerGetProfile = async () => {
  const response = await axios.get(`${AUTH_BASE}/me`, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerGetDashboard = async () => {
  const response = await axios.get(`${PARTNER_BASE}/dashboard`, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerGetUsage = async () => {
  const response = await axios.get(`${PARTNER_BASE}/usage`, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerGetSessions = async ({
  status,
  role,
  date_from,
  date_to,
  sort,
  limit,
  offset,
} = {}) => {
  const response = await axios.get(`${PARTNER_BASE}/sessions`, {
    headers: authHeaders(),
    params: { status, role, date_from, date_to, sort, limit, offset },
  });
  return response;
};

export const partnerGetKeys = async () => {
  const response = await axios.get(`${PARTNER_BASE}/keys`, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerCreateApiKey = async () => {
  const response = await axios.post(`${AUTH_BASE}/keys`, {}, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerGetBilling = async () => {
  const response = await axios.get(`${PARTNER_BASE}/billing`, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerGetWebhook = async () => {
  const response = await axios.get(`${PARTNER_BASE}/webhook`, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerSetWebhook = async (data) => {
  const response = await axios.put(`${PARTNER_BASE}/webhook`, data || {}, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerUpdateBranding = async (data) => {
  const response = await axios.put(`${PARTNER_BASE}/branding`, data, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerUploadLogo = async (data) => {
  const response = await axios.post(`${PARTNER_BASE}/branding/logo`, data, {
    headers: authHeaders(),
  });
  return response;
};

export const partnerGetBranding = async () => {
  const response = await axios.get(`${PARTNER_BASE}/branding`, {
    headers: authHeaders(),
  });
  return response;
};

// Invite links used to be built on awasource.dev, which does not resolve.
// Until every API process is restarted, rewrite those onto the site the
// partner is actually using. The assessment route lives on this frontend.
export const workingAssessmentUrl = (url) => {
  if (!url || typeof window === "undefined") return url;
  try {
    const parsed = new URL(url);
    if (parsed.hostname === "awasource.dev" || parsed.hostname === "www.awasource.dev") {
      return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    return url;
  }
  return url;
};

export const partnerGetTests = async () => {
  const response = await axios.get(`${partnerBase}/api/tests`, {
    headers: authHeaders(),
  });
  return response;
};

// Role dropdowns on Candidate Fit and Test Sessions. Psychometric rubric
// roles come back as kind "sjt"; older payloads are multiple-choice tests
// with no kind. Credit may be credit_cost or creditCost.
export const rolesFromTestsResponse = (res) => {
  const raw = res?.data?.data ?? res?.data ?? [];
  const tests = Array.isArray(raw) ? raw : [];
  const byRole = new Map();
  tests.forEach((t) => {
    const status = String(t?.status || "").toLowerCase();
    if (status && status !== "active") return;
    const name = displayRole(t?.role);
    const key = roleKey(name);
    if (!key || byRole.has(key)) return;
    const credits = Number(t.credit_cost ?? t.creditCost ?? 0);
    byRole.set(key, {
      t: name,
      c: Number.isFinite(credits) ? credits : 0,
      kind: t.kind === "sjt" ? "sjt" : "mcq",
      group: String(t.group || t.roleGroup || "").trim(),
    });
  });
  return Array.from(byRole.values()).sort((a, b) => a.t.localeCompare(b.t));
};

// --- added for completion: reveal-once, accept-invite, key rotate/revoke, assign, candidate-fit ---
export const inspectReveal = async (t) => {
  const r = await axios.get(`${PARTNER_BASE}/reveal`, { params: { t } });
  return r.data?.data ?? r.data;
};
export const redeemReveal = async (t) => {
  const r = await axios.post(`${PARTNER_BASE}/reveal`, { t });
  return r.data?.data ?? r.data;
};
export const partnerAcceptInvite = async (data) => {
  const r = await axios.post(`${PARTNER_BASE}/accept-invite`, data);
  return r.data?.data ?? r.data;
};
export const partnerCreateEnrollmentLink = async (data) => {
  const r = await axios.post(`${PARTNER_BASE}/enrollment-links`, data, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};
// The partner's 50 most recent shared links, newest first.
export const partnerListEnrollmentLinks = async () => {
  const r = await axios.get(`${PARTNER_BASE}/enrollment-links`, { headers: authHeaders() });
  const rows = r.data?.data ?? r.data;
  return Array.isArray(rows) ? rows : [];
};
// env is "live" | "sandbox" — rotate/revoke act on one key pair at a time.
export const partnerRotateKey = async (env) => {
  const r = await axios.post(`${PARTNER_BASE}/keys/rotate`, { env }, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};
export const partnerRevokeKey = async (env) => {
  const r = await axios.post(`${PARTNER_BASE}/keys/revoke`, { env }, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};

// Session creation/lookup is mounted at /api/sessions (shared with API-key
// callers), not /api/partner/sessions — only listing lives under /partner.
export const partnerCreateSession = async ({ candidate_email, candidate_name, role, scheduled_date, scheduled_time }) => {
  const r = await axios.post(
    `${SESSIONS_BASE}`,
    { candidate_email, candidate_name, role, scheduled_date, scheduled_time },
    { headers: authHeaders() },
  );
  return r.data?.data ?? r.data;
};
export const partnerGetSession = async (id) => {
  const r = await axios.get(`${SESSIONS_BASE}/${id}`, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};

export const partnerSendTestWebhook = async () => {
  const r = await axios.post(`${PARTNER_BASE}/webhook/test`, {}, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};

// ---- Candidate Fit (job specs, pool, shortlist, send) ----
export const listJobSpecs = async () => {
  const r = await axios.get(`${CANDIDATE_FIT_BASE}/job-specs`, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};
export const createJobSpec = async (data) => {
  const r = await axios.post(`${CANDIDATE_FIT_BASE}/job-specs`, data, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};
export const updateJobSpec = async (id, data) => {
  const r = await axios.put(`${CANDIDATE_FIT_BASE}/job-specs/${id}`, data, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};
export const deleteJobSpec = async (id) => {
  const r = await axios.delete(`${CANDIDATE_FIT_BASE}/job-specs/${id}`, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};
export const uploadFitCandidates = async (jobSpecId, candidates) => {
  const r = await axios.post(
    `${CANDIDATE_FIT_BASE}/upload`,
    { job_spec_id: jobSpecId, candidates },
    { headers: authHeaders() },
  );
  return r.data?.data ?? r.data;
};
// CV files (PDF/DOCX) → multipart. The API reads each file and scores it
// against the spec in the background (rows come back "pending"); files it
// can't read come back in `failed` with a reason. At most 50 files per call.
export const uploadFitCvs = async (jobSpecId, files, onUploadProgress) => {
  const form = new FormData();
  form.append("job_spec_id", jobSpecId);
  files.forEach((f) => form.append("cvs", f, f.name));
  const r = await axios.post(`${CANDIDATE_FIT_BASE}/upload-cvs`, form, {
    headers: authHeaders(),
    onUploadProgress,
  });
  return r.data?.data ?? r.data;
};
export const removeFitCandidate = async (candidateId) => {
  const r = await axios.delete(`${CANDIDATE_FIT_BASE}/${candidateId}`, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};
export const listFitPool = async (jobSpecId, sort) => {
  const r = await axios.get(`${CANDIDATE_FIT_BASE}/pool`, {
    params: { job_spec_id: jobSpecId, sort },
    headers: authHeaders(),
  });
  return r.data?.data ?? r.data;
};
export const toggleFitShortlist = async (candidateId) => {
  const r = await axios.post(`${CANDIDATE_FIT_BASE}/${candidateId}/shortlist`, {}, { headers: authHeaders() });
  return r.data?.data ?? r.data;
};
export const exportFitShortlist = async (jobSpecId) => {
  const r = await axios.get(`${CANDIDATE_FIT_BASE}/shortlist/export`, {
    params: { job_spec_id: jobSpecId },
    headers: authHeaders(),
    responseType: "blob",
  });
  return r.data;
};
export const sendFitAssessments = async (candidateIds, role) => {
  const r = await axios.post(
    `${CANDIDATE_FIT_BASE}/send-assessments`,
    { candidate_ids: candidateIds, role },
    { headers: authHeaders() },
  );
  return r.data?.data ?? r.data;
};
